import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faTimes } from '@fortawesome/pro-regular-svg-icons/faTimes';
import { faWifi } from '@fortawesome/pro-solid-svg-icons/faWifi';
import BROADBAND_INFO from '@propertypal/shared/src/constants/broadband';
import useInView from '@propertypal/shared/src/hooks/useInView';
import Fibrus from '@propertypal/shared/src/resources/logos/fibrus-logo.svg';
import FibrusWhite from '@propertypal/shared/src/resources/logos/fibrus-white.png';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { FIBRUS_PINK } from '@propertypal/shared/src/styles/theme';
import { Property } from '@propertypal/shared/src/types/property';
import boolCheck from '@propertypal/shared/src/utils/bool-check';
import showBroadbandChecker, { showBroadbandInterest } from '@propertypal/shared/src/utils/property/broadband';
import formatBroadbandData from '@propertypal/shared/src/utils/property/formatBroadbandData';
import { validEmail, validPhoneCharacters } from '@propertypal/shared/src/utils/regex';
import { useFormik } from 'formik';
import React, { FunctionComponent, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTheme } from 'styled-components';
import * as yup from 'yup';
import Button from '../buttons/Button';
import FontAwesomeIcon from '../icons/FontAwesomeIcon';
import TextInput from '../inputs/TextInput';
import { Row } from '../layout';
import WhiteModal from '../modal/WhiteModal';
import InfoIcon from '../schools/InfoIcon';
import { getCaptchaKey } from '../security/Recaptcha';
import { openToast } from '../toast/ToastContext';
import { LinkText, MidHeading, SubHeading, Text } from '../typography';
import {
  Container,
  FibrusHeading,
  FibrusLogo,
  InterestForm,
  InterestFormBox,
  InterestFormHeader,
  InterestFormInputs,
  InterestFormLogo,
  InterestFormTitle,
  NoBroadbandBox,
  NoBroadbandContent,
  StyledTable,
  Title,
  TitleContainer,
} from './BroadbandTable.style';

interface Props {
  property: Property;
}

const interestValidationSchema = yup
  .object({
    firstName: yup.string().trim().required('Please enter your first name'),
    lastName: yup.string().trim().required('Please enter your last name'),
    email: yup
      .string()
      .trim()
      .test('valid-email', 'Please enter a valid email address', (value) => !value || validEmail.test(value)),
    telephone: yup
      .string()
      .trim()
      .test(
        'valid-telephone',
        'Please enter a valid phone number',
        (value) => !value || validPhoneCharacters.test(value),
      ),
  })
  .test('email-or-telephone', function validateContactMethod(values) {
    if (values?.email?.trim() || values?.telephone?.trim()) return true;

    return this.createError({
      path: 'telephone',
      message: 'Please enter your email or telephone number',
    });
  });

const BroadbandTable: FunctionComponent<Props> = (props) => {
  const { broadbandSpeed, saleType, displayAddress } = props.property;
  const theme = useTheme();
  const [tooltipWindow, setTooltipWindow] = useState('');
  const [success, setSuccess] = useState<boolean>(false);
  const [sending, setSending] = useState(false);
  const fibrusOption = tooltipWindow === 'Fibrus';
  const containerRef = useRef<HTMLDivElement>(null);
  const broadbandChecker = showBroadbandChecker(props.property);
  const dispatch = useDispatch();

  const interestForm = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      telephone: '',
      email: '',
    },
    validationSchema: interestValidationSchema,
    onSubmit: async (values) => {
      setSending(true);

      try {
        const recaptcha = await getCaptchaKey('enquire');

        await axios(
          {
            url: `${API_URL}/enquire`,
            method: 'POST',
            data: {
              name: `${values.firstName} ${values.lastName}`,
              email: values.email,
              telephone: values.telephone,
              comments: 'Fibrus Enquiry',
              reason: 'enquireFibrus',
              recaptcha,
              propertyId: props.property.id,
            },
          },
          dispatch,
          undefined,
        );
        trackGaEvent('fibrus_enquiry_submit', { page: 'PDP' });

        setSuccess(true);
      } catch (err) {
        openToast({ title: 'Error', message: 'There was an issue submitting your request. Please try again later.' });
        setSending(false);
      }
    },
  });

  const handleTooltipClick = (type: string) => () => {
    trackGaEvent('broadband_tooltip_click', {
      page: 'PDP',
      broadbandType: type,
    });
    setTooltipWindow(type);
  };

  useInView(containerRef, { threshold: 0.5 }, () => {
    trackGaEvent('broadband_speeds_impression', {
      page: 'PDP',
      fibrus: boolCheck(broadbandSpeed?.fibrusAvailable),
    });
  });

  if (broadbandChecker) {
    return (
      <NoBroadbandBox ref={containerRef}>
        <div>
          <SubHeading color={theme.white}>Broadband Speed Checker</SubHeading>

          <NoBroadbandContent>
            <FontAwesomeIcon icon={faWifi} color={theme.white} size={20} className="fibrus-desktop-icon" />
            <div>
              <Text color={theme.white}>
                We don&apos;t currently have broadband availability information for this property. Check the postcode
                with our partner <b>Fibrus Broadband</b> for their availability in the area and register your interest
                to receive updates on future connectivity.
              </Text>

              <a
                href="https://fibrus.com/availability/?utm_source=propertypal"
                target="_blank"
                rel="noreferrer"
                onClick={() => {
                  trackGaEvent('fibrus_check_postcode_click', { page: 'PDP', fibrus: 'N/A', location: 'PDP Body' });
                }}
              >
                Check Your Postcode
              </a>
            </div>
          </NoBroadbandContent>
        </div>

        <div>
          <Text color={theme.white} fontSize={12} mb={10}>
            In partnership with
          </Text>
          <Fibrus style={{ width: 110 }} />
        </div>
      </NoBroadbandBox>
    );
  }

  if (broadbandSpeed) {
    const data = formatBroadbandData(broadbandSpeed);

    return (
      <>
        {saleType.key === 'rent' && broadbandSpeed.fibrusAvailable && (
          <Text fontSize={12} fontWeight="bold" mt={30}>
            Important: Contact the letting agent before committing to this property to confirm whether landlord
            permission can be granted for broadband installation. Installation may require physical work to the property
          </Text>
        )}

        <Container ref={containerRef}>
          <TitleContainer>
            <div style={{ flex: 1 }}>
              <SubHeading fontSize={24} mb={5}>
                Broadband Checker
              </SubHeading>
              <Row>
                <FontAwesomeIcon icon={faWifi} style={{ fontSize: 16, marginRight: 10, marginTop: 5 }} />

                <div>
                  <Title>Fixed-line broadband services at {displayAddress}</Title>

                  {broadbandSpeed.estimated && (
                    <Text fontSize={12}>
                      Exact broadband speeds are unknown for this property, we&apos;ve estimated the likely speed based
                      on other properties in the postcode.
                    </Text>
                  )}
                </div>
              </Row>
            </div>

            <a
              data-testid="broadband-fibrus"
              href="https://fibrus.com/propertypal-broadband/"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                trackGaEvent('broadband_fibrus_click', {
                  page: 'PDP',
                  location: 'Logo',
                  fibrus: boolCheck(broadbandSpeed?.fibrusAvailable),
                });
              }}
            >
              <Text align="center" mb={5} fontSize={12}>
                In partnership with
              </Text>
              <FibrusLogo w={122} />
            </a>
          </TitleContainer>

          <StyledTable>
            <thead>
              <tr>
                <th>Broadband type</th>
                <th>Highest available download speed</th>
                <th>Highest available upload speed</th>
                <th>Availability</th>
              </tr>
            </thead>

            <tbody>
              {data.map((row) => (
                <tr key={row.label}>
                  <td>
                    {row.fibrus ? <FibrusHeading>{row.label}</FibrusHeading> : <>{row.label}</>}{' '}
                    {row.fibrus && (
                      <a
                        href="https://fibrus.com/propertypal-broadband/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => {
                          trackGaEvent('broadband_fibrus_click', { page: 'PDP', fibrus: true, location: 'Logo' });
                        }}
                        data-testid="broadband-fibrus-option"
                        style={{ display: 'inline-block' }}
                      >
                        <FibrusLogo w={58} />
                      </a>
                    )}
                    <InfoIcon
                      testID={`broadband-${row.label}`}
                      onClick={handleTooltipClick(row.fibrus ? 'Fibrus' : row.label)}
                      color={theme.textDark}
                      style={row.fibrus ? { verticalAlign: 'top', paddingTop: 2 } : undefined}
                    />
                  </td>
                  <td>{row.download}</td>
                  <td>{row.upload}</td>
                  <td>
                    <FontAwesomeIcon
                      icon={row.availability ? faCheck : faTimes}
                      color={row.availability ? theme.green : theme.red}
                      style={{ fontSize: 24 }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </StyledTable>

          {broadbandSpeed.fibrusAvailable && (
            <InterestFormBox>
              <InterestFormHeader>
                <InterestFormTitle>
                  {success ? 'Thank you, your interest has been submitted' : "Let Fibrus know you're interested"}
                </InterestFormTitle>
                <InterestFormLogo src={FibrusWhite.src} alt="Fibrus" />
              </InterestFormHeader>

              {!success && (
                <InterestForm onSubmit={interestForm.handleSubmit}>
                  <InterestFormInputs>
                    <TextInput
                      name="firstName"
                      placeholder="First name"
                      value={interestForm.values.firstName}
                      onValueChange={interestForm.handleChange('firstName')}
                      error={interestForm.touched.firstName && interestForm.errors.firstName}
                      testID="fibrus-first-name"
                    />
                    <TextInput
                      name="lastName"
                      placeholder="Last name"
                      value={interestForm.values.lastName}
                      onValueChange={interestForm.handleChange('lastName')}
                      error={interestForm.touched.lastName && interestForm.errors.lastName}
                      testID="fibrus-last-name"
                    />
                    <TextInput
                      name="email"
                      placeholder="Email"
                      type="email"
                      value={interestForm.values.email}
                      onValueChange={interestForm.handleChange('email')}
                      error={interestForm.touched.email && interestForm.errors.email}
                      testID="fibrus-email"
                    />
                    <TextInput
                      name="telephone"
                      placeholder="Telephone"
                      type="tel"
                      value={interestForm.values.telephone}
                      onValueChange={interestForm.handleChange('telephone')}
                      error={interestForm.touched.telephone && interestForm.errors.telephone}
                      testID="fibrus-telephone"
                    />

                    <Button
                      color={FIBRUS_PINK}
                      type="submit"
                      loading={sending}
                      disabled={sending}
                      testID="fibrus-submit"
                    >
                      Submit
                    </Button>
                  </InterestFormInputs>

                  <Text color={theme.white} fontSize={14} mt={10}>
                    By clicking the “Submit” button you confirm that you consent to PropertyPal sharing your information
                    with Fibrus and to Fibrus using, storing, processing and transferring your personal data for the
                    purposes and in the manner outlined in this{' '}
                    <a
                      href="/fibrus-privacy-notice.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline' }}
                    >
                      Privacy Statement
                    </a>
                    .
                  </Text>
                </InterestForm>
              )}
            </InterestFormBox>
          )}

          <Text fontSize={12} color={theme.backgroundMid}>
            The speeds indicated on our checker represent the maximum estimated fixed-line speeds as predicted by the
            network operators providing services in a particular area. Please note that these are estimates, and actual
            service availability and speeds received at a given property may differ.
          </Text>

          <Text fontSize={12} mt={10} color={theme.backgroundMid}>
            Besides fixed-line services, options for 4G, 5G, or satellite broadband may be available at the address,
            contingent upon the necessary hardware. These alternatives are not reflected in the checker&apos;s
            estimates.
          </Text>

          {saleType.key === 'rent' && (
            <Text fontSize={12} mt={10} color={theme.backgroundMid}>
              Broadband availability information is provided for guidance purposes only. Broadband services are not
              included with this property unless explicitly stated in the tenancy agreement. Installation of broadband
              services may require landlord consent and could involve physical alterations to the property. Please
              consult your tenancy agreement and contact the letting agent to confirm what services are available.
            </Text>
          )}

          {saleType.key !== 'rent' && (
            <Text fontSize={12} mt={10} color={theme.backgroundMid}>
              Broadband availability information is provided for guidance purposes only.
            </Text>
          )}

          <WhiteModal show={!!tooltipWindow} onClose={() => setTooltipWindow('')} maxWidth={500}>
            {fibrusOption && (
              <a
                href="https://fibrus.com/propertypal-broadband/"
                target="_blank"
                rel="noopener noreferrer"
                style={{ marginBottom: 10, textAlign: 'center', display: 'block' }}
                onClick={() => {
                  trackGaEvent('broadband_fibrus_click', { page: 'PDP', fibrus: true, location: 'tooltip' });
                }}
                data-testid="fibrus-modal-logo"
              >
                <FibrusLogo w={100} />
              </a>
            )}
            <MidHeading align={fibrusOption ? 'center' : undefined}>
              {fibrusOption ? 'Great news!' : tooltipWindow}
            </MidHeading>
            <Text align={fibrusOption ? 'center' : undefined} mt={10}>
              {BROADBAND_INFO[fibrusOption ? 'Fibrefast with' : tooltipWindow]}
            </Text>
            {fibrusOption && (
              <>
                <Text align="center" mt={10}>
                  <LinkText
                    href="https://fibrus.com/propertypal-broadband/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackGaEvent('fibrus_pdp_ad_click', { page: 'PDP', fibrus: true, location: 'Find out more' });
                    }}
                    data-testid="fibrus-find-out-more"
                  >
                    Find out more
                  </LinkText>
                </Text>
                <Text align="center" mt={10} fontSize={12}>
                  *Fibrus Total Home Wi-Fi: Guarantees 10Mbps in eligible rooms, subject to restrictions, or free
                  cancellation. See full T&Cs at{' '}
                  <LinkText fontSize={12} href="https://www.fibrus.com/terms" target="_blank" rel="noreferrer">
                    fibrus.com/terms
                  </LinkText>
                  .
                </Text>
              </>
            )}
          </WhiteModal>
        </Container>

        {showBroadbandInterest(props.property) && (
          <NoBroadbandBox>
            <div>
              <SubHeading color={theme.white}>Fibrus Broadband Availability</SubHeading>

              <NoBroadbandContent>
                <FontAwesomeIcon icon={faWifi} color={theme.white} size={20} className="fibrus-desktop-icon" />
                <div>
                  <Text color={theme.white}>
                    It doesn&apos;t look like this property is connectable to <b>Fibrus Broadband</b> right now. Head to
                    their website to find out more, or to register your interest and receive updates if this property
                    becomes connectable in the future.
                  </Text>

                  <a
                    href="https://fibrus.com/propertypal-broadband"
                    target="_blank"
                    rel="noreferrer"
                    onClick={() => {
                      trackGaEvent('fibrus_pdp_ad_click', { page: 'PDP', fibrus: false, location: 'Find out more' });
                    }}
                  >
                    Find out more
                  </a>
                </div>
              </NoBroadbandContent>
            </div>

            <div>
              <Text color={theme.white} fontSize={12} mb={10}>
                In partnership with
              </Text>
              <Fibrus style={{ width: 110 }} />
            </div>
          </NoBroadbandBox>
        )}
      </>
    );
  }

  return null;
};

export default BroadbandTable;
